import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCustomerById,
  getCustomerSocieties,
  getAllPromotions,
} from '../services/api';
import type { CustomerRecord, Promotion } from '../services/api';
import './AdminCustomerDetail.css';

const AdminCustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<CustomerRecord | null>(null);
  const [societies, setSocieties] = useState<Array<{ id: number; name: string }>>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const customerId = id ? parseInt(id, 10) : NaN;
    if (Number.isNaN(customerId)) {
      setError('Invalid customer ID');
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [custRes, socRes, promoRes] = await Promise.all([
          getCustomerById(customerId),
          getCustomerSocieties(customerId),
          getAllPromotions(),
        ]);
        setCustomer(custRes.data);
        setSocieties(socRes.data || []);
        const allPromos = promoRes.data || [];
        const customerPromos = allPromos.filter(
          (p) => (p as Promotion & { created_by_customer_id?: number | null }).created_by_customer_id === customerId
        );
        setPromotions(customerPromos);
      } catch (e) {
        setError('Failed to load customer');
        setCustomer(null);
        setPromotions([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = { story: 'Story', spotlight: 'Spotlight', category: 'Category' };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="admin-customer-detail">
        <div className="customer-detail-loading">Loading...</div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="admin-customer-detail">
        <div className="customer-detail-error">
          <p>{error || 'Customer not found'}</p>
          <button type="button" className="btn-back" onClick={() => navigate('/admin/customers')}>
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-customer-detail">
      <div className="customer-detail-header">
        <button type="button" className="btn-back" onClick={() => navigate('/admin/customers')}>
          ← Back to Customers
        </button>
        <h2>Customer Details</h2>
      </div>

      <section className="customer-detail-card">
        <h3>{customer.customer_name}</h3>
        {customer.business_name && <p className="business-name">{customer.business_name}</p>}
        <dl className="customer-detail-dl">
          <dt>Email</dt>
          <dd>{customer.email}</dd>
          <dt>Credits</dt>
          <dd>{customer.credits}</dd>
          {customer.address && (
            <>
              <dt>Address</dt>
              <dd>{customer.address}</dd>
            </>
          )}
          {(customer.city || customer.state || customer.pin_code) && (
            <>
              <dt>Location</dt>
              <dd>
                {[customer.city, customer.state, customer.pin_code].filter(Boolean).join(', ') || '–'}
              </dd>
            </>
          )}
          {societies.length > 0 && (
            <>
              <dt>Societies</dt>
              <dd>{societies.map((s) => s.name).join(', ')}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="customer-promotions-section">
        <h3>Promotions ({promotions.length})</h3>
        {promotions.length === 0 ? (
          <p className="no-promotions">No promotions created under this account yet.</p>
        ) : (
          <div className="customer-promotions-table-wrap">
            <table className="customer-promotions-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Brand</th>
                  <th>Status</th>
                  <th>Valid From</th>
                  <th>Valid To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.title}</td>
                    <td>
                      <span className={`type-badge type-${p.promotion_type}`}>
                        {getTypeLabel(p.promotion_type)}
                      </span>
                    </td>
                    <td>{p.brand_name || '–'}</td>
                    <td>
                      <span className={`status-badge ${p.is_active === 1 ? 'active' : 'inactive'}`}>
                        {p.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(p.valid_from).toLocaleDateString()}</td>
                    <td>{new Date(p.valid_to).toLocaleDateString()}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-edit-sm"
                        onClick={() => navigate(`/admin/promotions?edit=${p.id}`)}
                      >
                        View / Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminCustomerDetail;
